import dayjs from 'dayjs';
import { IStatusHistoryItem, OrdersStatus } from '@/models/orders.model';
import { AWAITING_OVERDUE_THRESHOLD_DAYS } from '../constants';

/**
 * Checks if the given date is considered overdue based on the business threshold.
 *
 * @param awaitingSince - ISO date string when the order started awaiting, or null
 * @returns true if the date is AWAITING_OVERDUE_THRESHOLD_DAYS or more days in the past
 *
 * @example
 * isAwaitingOverdue('2024-01-01T00:00:00Z') // true if 3+ days ago
 * isAwaitingOverdue(null) // false
 * isAwaitingOverdue('invalid-date') // false
 */
export const isAwaitingOverdue = (awaitingSince: string | null | undefined): boolean => {
  if (!awaitingSince) {
    return false;
  }

  const awaitingDate = dayjs(awaitingSince);

  if (!awaitingDate.isValid()) {
    return false;
  }

  const daysDiff = dayjs().diff(awaitingDate, 'day');

  return daysDiff >= AWAITING_OVERDUE_THRESHOLD_DAYS;
};

/**
 * Extracts the awaiting status date from order status history.
 *
 * @param statusHistory - Array of status history items from API
 * @returns ISO date string when order entered "awaiting" status, or null if not found/invalid
 *
 * @note This function assumes status_history is ordered chronologically by the API.
 *       If the API changes the order, this logic may need to be updated.
 */
export const getAwaitingDateFromHistory = (statusHistory: IStatusHistoryItem[] | null | undefined): string | null => {
  if (!statusHistory || !Array.isArray(statusHistory) || statusHistory.length === 0) {
    return null;
  }

  const awaitingEntry = statusHistory.find((entry) => entry.status === OrdersStatus.AWAITING);

  if (!awaitingEntry?.date) {
    return null;
  }

  // Validate the date is parseable
  const parsedDate = dayjs(awaitingEntry.date);
  if (!parsedDate.isValid()) {
    return null;
  }

  return awaitingEntry.date;
};
